import React, { ComponentProps, useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Button, Collapse, Container, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { IdentityDocumentCard, useMeetingData, zMeetingData } from "./ApplicationMeetingData";
import TestApplicationAlert from "../components/TestApplicationAlert";

export const useLinkToMeeting = () => {
  const { application_id } = useParams<{ application_id?: string }>()

  if (!application_id) throw new Error('application_id is required')

  return {
    linkTo: (path: string) => `/applications/${application_id}/meeting/${path}`,
    application_id,
  }
}

export const zMeetingDataWithResolversGroup1 = zMeetingData // No validation in the applicant system
export const zMeetingDataWithResolversGroup2 = zMeetingData // No validation in the applicant system

/**
 * With ApplicationMeetingGroup files being common across applications, but this file being specific to each application,
 * this function filters the props to only those relevant to this application.
 */
export const filterIdentityDocumentCardProps = (props: Partial<ComponentProps<typeof IdentityDocumentCard>>): Partial<ComponentProps<typeof IdentityDocumentCard>> => {
  return {
    selected: props.selected,
    onSelect: props.onSelect,
  }
}

export const Element: React.FC = () => {
  const { linkTo, application_id } = useLinkToMeeting()

  const { navigateWithMeetingData, setMeetingData } = useMeetingData()

  // Load the application and if the MeetingData is empty, populate it with what is in the application
  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  useEffect(() => {
    if (getEBulkApplicationByID.data?.documents_applicant && Object.keys(setMeetingData).length === 0) {
      setMeetingData(getEBulkApplicationByID.data.documents_applicant)
    }
  }, [getEBulkApplicationByID.data, setMeetingData])

  // UI State
  const [showDocumentGuidance, setShowDocumentGuidance] = useState(false)

  return <>
    <Helmet>
      <title>Application Meeting</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h1" variant="h5" marginBottom={3}>
          Application Meeting
        </Typography>
      </Box>
      <Typography variant="body1" marginBottom={2}>
        You now need to attend a meeting with your employer, where they will check your identity using a set of documents.{' '}
      </Typography>
      <Typography variant="body1" marginBottom={2}>
        This page will help you identify which documents you need to bring.{' '}
      </Typography>
      <Typography variant="body1" marginBottom={3}>
        There are 3 groups (1, 2a, and 2b) of documents.{' '}
        If you cannot provide any ID from Group 1, you must contact us.
      </Typography>
      <Typography variant="body1" marginBottom={1}>
        <strong>Please note</strong> all documents must be original, photocopies and documents downloaded from the internet cannot be accepted.{' '}
        <Button
          variant="text"
          color="primary"
          onClick={() => { setShowDocumentGuidance(currentState => !currentState) }}
          sx={{ padding: 0, minWidth: 'auto', textTransform: 'none' }}
        >
          Why are specific documents required?
        </Button>
      </Typography>
      <Collapse in={showDocumentGuidance}>
        <Alert id="document-guidance" severity="info" sx={{ m: 0 }} onClose={() => { setShowDocumentGuidance(false) }}>
          <AlertTitle>Document Verification Information</AlertTitle>
          <Typography variant="body2" marginBottom={1}>
            In order to progress with the application, the applicant must provide sufficient ID to validate their application.
          </Typography>

          <Typography variant="body2" marginBottom={1}>
            Guidance has been produced on the type and range of ID documents that must be seen to validate the identity of the applicant.{' '}
            For full guidance consult{' '}
            <a href="https://www.gov.uk/government/publications/dbs-identity-checking-guidelines/id-checking-guidelines-for-standardenhanced-dbs-check-applications-from-1-july-2021">
              🔗 DBS Guidance – Standard & Enhanced
            </a>
          </Typography>
          <ul>
            <li>A minimum of three documents must be witnessed. At least one document should be from Group 1. If you cannot provide any ID from Group 1, you must contact us.</li>
            <li>At least one document must confirm the date of birth.</li>
            <li>At least one document must confirm the current address.</li>
            <li>All documents must be original. Photocopies and documents downloaded from the internet are not acceptable.</li>
            <li>All personal details provided by the applicant should ensure the full and correct name and address history has been validated.</li>
            <li>Failure to validate the information correctly may lead to the check being invalid.</li>
          </ul>
        </Alert>
      </Collapse>
      <Button
        data-testid="back-button"
        variant="contained"
        color="inherit"
        onClick={() => {
          window.history.back();
        }}
        sx={{ mt: 3, mr: 2, whiteSpace: 'nowrap'}}
      >
        &lt; Back
      </Button>
      <Button variant="contained" onClick={() => { navigateWithMeetingData(linkTo('group1')) }} sx={{ mt: 3 }}>
        Select Group 1 Documents
      </Button>
    </Container>
  </>
}

export default Element
